import { Link } from "react-router";
import { useState, useEffect } from "react";
import {
  BarChart,
  History,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { useSession } from "@/admin/lib/auth-client";

interface SubCurrentUsageProps {
  subscriptionId: string;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} ${diffInMinutes === 1 ? 'min' : 'mins'} ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
}

function formatTimestamp(date: Date): string {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  const h = date.getHours().toString().padStart(2, '0');
  const min = date.getMinutes().toString().padStart(2, '0');
  return `${d}/${m}/${y} ${h}h${min}`;
}

export function AdminSubCurrentUsage({ subscriptionId }: SubCurrentUsageProps) {
  const { data: session } = useSession();

  const { data: currentUsage } = trpc.subscription.usage.currentUsage.useQuery(
    { subscriptionId },
    { enabled: !!session && !!subscriptionId }
  );

  const [relativeTime, setRelativeTime] = useState<string>("");

  useEffect(() => {
    if (currentUsage?.usage?.timestamp) {
      const date = new Date(currentUsage.usage.timestamp);
      setRelativeTime(formatRelativeTime(date));

      const interval = setInterval(() => {
        setRelativeTime(formatRelativeTime(date));
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [currentUsage?.usage?.timestamp]);

  const { data: pastUsage } = trpc.subscription.usage.pastUsage.useQuery(
    { subscriptionId },
    { enabled: !!session && !!subscriptionId }
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="h-5 w-5" />
              Current Usage
            </CardTitle>
            <CardDescription>
              {currentUsage && currentUsage.startDate && currentUsage.endDate
                ? `API usage for the current billing period (${new Date(currentUsage.startDate).toLocaleDateString()} - ${new Date(currentUsage.endDate).toLocaleDateString()})`
                : "API usage will appear here once you start using the API"}
            </CardDescription>
            {currentUsage?.usage?.timestamp && (
              <div className="text-xs text-chromium-mist/60 mt-1">
                Last updated: {formatTimestamp(new Date(currentUsage.usage.timestamp))} ({relativeTime})
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {(!currentUsage || !currentUsage.usage || !currentUsage.usage.splits || currentUsage.usage.splits.length === 0) ? (
            <div className="text-center py-8">
              <div className="text-chromium-mist mb-2">No Usage Yet</div>
              <p className="text-sm text-chromium-mist/70">
                Start making API requests to see your usage statistics here.
              </p>
            </div>
          ) : (
            currentUsage.usage.splits.map((usageEntry: { type: string; limit: number; used: number }) => {
              const percentage = usageEntry.limit > 0 ? (usageEntry.used / usageEntry.limit) * 100 : 0;
              const remaining = usageEntry.limit - usageEntry.used;

              return (
                <div key={usageEntry.type} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-silica capitalize">
                      {usageEntry.type} requests
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-chromium-mist">
                      {usageEntry.used.toLocaleString()} / {usageEntry.limit.toLocaleString()}
                    </span>
                    <span className="text-silica">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                  <div className="h-2 bg-slate-core rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-yellow-500' : 'bg-neon-green'
                        }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-chromium-mist text-right">
                    {remaining.toLocaleString()} remaining
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
